import { useCallback, useEffect, useState } from 'react';
import { claimPairing, deleteDevice, fetchDevices, fetchSettings, saveSettings } from '../api.js';
import { relativeTime } from '../../../shared/time.js';
import { SIGNAGE_URL } from '../config.js';
import { useToast } from '../Toaster.jsx';
import SharedDisplayPanel from './SharedDisplayPanel.jsx';

// What the form shows before the account's own settings arrive, and the floor
// under a response from an account saved before a field existed.
//
// This mirrors models.DefaultSettings on the Go side - the FALLBACK - and not
// models.NewAccountSettings, which is what a new account is created holding.
// The two differ on the theme now, and this is the one that answers "the row
// did not say", which is the only question a floor is ever asked.
const DEFAULT_SETTINGS = {
  sortOrder: 'dateAdded',
  sortDirection: 'desc',
  bookshelfLayout: 'stacked',
  bookshelfTheme: 'classic',
};

// Pulled out of the URL once, on the first render that reaches this panel.
// Removing it immediately is what stops a refresh - or StrictMode's second
// effect pass - from trying to claim the same code twice.
function takePairingCodeFromURL() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('pair');
  if (!code) return '';

  params.delete('pair');
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
  );
  return code;
}

// Displays and the settings they render with. They live together because they
// are the same subject from two angles: which screens are attached, and what
// those screens show.
export default function DisplaysPanel() {
  const [devices, setDevices] = useState([]);
  const [pairing, setPairing] = useState({ code: '', name: '' });
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const notify = useToast();
  const [busy, setBusy] = useState('');

  const reloadDevices = useCallback(() => {
    fetchDevices()
      .then(setDevices)
      .catch((error) => console.error('Failed to load displays', error));
  }, []);

  // A QR scan lands here already carrying its code, so claim it rather than
  // making someone retype what they just scanned. The signage app is showing
  // this code right now, so it is still valid.
  useEffect(() => {
    const code = takePairingCodeFromURL();
    if (!code) return;

    setBusy('pair');
    claimPairing(code, '')
      .then((result) => notify(`Paired ${result.device.name}.`))
      .catch((error) => notify(`Pairing failed: ${error.message}`, { tone: 'error' }))
      .finally(() => {
        setBusy('');
        reloadDevices();
      });
  }, [reloadDevices]);

  useEffect(() => {
    reloadDevices();
    fetchSettings()
      .then((data) => setSettings({ ...DEFAULT_SETTINGS, ...data }))
      .catch((error) => console.error('Failed to load settings', error));
  }, [reloadDevices]);

  async function handleClaim(event) {
    event.preventDefault();
    setBusy('pair');
    try {
      const result = await claimPairing(pairing.code, pairing.name);
      notify(`Paired ${result.device.name}.`);
      setPairing({ code: '', name: '' });
    } catch (error) {
      notify(`Pairing failed: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
      reloadDevices();
    }
  }

  async function handleRevoke(id) {
    setBusy(id);
    try {
      await deleteDevice(id);
      notify('Display removed.');
    } catch (error) {
      notify(`Remove failed: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
      reloadDevices();
    }
  }

  async function handleSaveSettings(event) {
    event.preventDefault();
    try {
      const data = await saveSettings(settings);
      if (data.saved) notify('Settings saved.');
      else notify('Unable to save settings.', { tone: 'error' });
    } catch (error) {
      notify(`Failed to save settings: ${error.message}`, { tone: 'error' });
    }
  }

  return (
    <>
      <SharedDisplayPanel />

      <section className="panel">
        <h2>Paired displays</h2>
        {devices.length === 0 ? (
          <p className="panel-hint">No displays paired yet.</p>
        ) : (
          <ul className="connection-list">
            {devices.map((device) => (
              <li key={device.id} className="connection-row">
                <div>
                  <strong>{device.name || device.id}</strong>
                  <p className="author">
                    paired <time dateTime={device.pairedAt} title={device.pairedAt}>
                      {relativeTime(device.pairedAt)}
                    </time>
                    {' · '}
                    {/* Every display paired before this field existed has no
                        value, and a screen that has been unplugged for a month
                        has no new one - so absent has to read as absent rather
                        than as a date at the epoch. */}
                    {device.lastSeenAt ? (
                      <>
                        last seen <time dateTime={device.lastSeenAt} title={device.lastSeenAt}>
                          {relativeTime(device.lastSeenAt)}
                        </time>
                      </>
                    ) : (
                      'not seen yet'
                    )}
                  </p>
                </div>
                <div className="connection-actions">
                  <button
                    type="button"
                    className="danger"
                    onClick={() => handleRevoke(device.id)}
                    disabled={busy === device.id}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Screens have no keyboard, so they show a code and the owner claims it
          here rather than signing in on the display itself. */}
      <form className="panel" onSubmit={handleClaim}>
        <h2>Pair a display</h2>
        <p className="panel-hint pairing-cta">
          Open <a href={SIGNAGE_URL} target="_blank" rel="noreferrer">
            {SIGNAGE_URL.replace(/^https?:\/\//, '')}
          </a> on a device or in another tab, then scan its QR code - or enter
          the code it shows below.
        </p>
        <label className="field">
          <span>Pairing code from the display</span>
          <input
            type="text"
            value={pairing.code}
            placeholder="ABC-DEF"
            onChange={(event) => setPairing({ ...pairing, code: event.target.value })}
          />
        </label>
        <label className="field">
          <span>Name this display</span>
          <input
            type="text"
            value={pairing.name}
            placeholder="Living room"
            onChange={(event) => setPairing({ ...pairing, name: event.target.value })}
          />
        </label>
        <button type="submit" className="save-button" disabled={busy === 'pair' || !pairing.code}>
          {busy === 'pair' ? 'Pairing…' : 'Pair display'}
        </button>
      </form>

      <form className="panel" onSubmit={handleSaveSettings}>
        <h2>Display settings</h2>
        <label className="field">
          <span>Sort books by</span>
          <select
            value={settings.sortOrder}
            onChange={(event) => setSettings({ ...settings, sortOrder: event.target.value })}
          >
            <option value="dateAdded">Date added</option>
            <option value="dateRead">Date read</option>
            <option value="shelfDate">Shelf-specific date</option>
            <option value="publishedYear">Publish date</option>
            <option value="rating">Rating</option>
            <option value="author">Author</option>
            <option value="title">Title</option>
          </select>
          <small className="field-hint">
            Goodreads stamps a date added on every book. A date read is set by hand, so
            books without one go to the end of the shelf. Shelf-specific date asks each
            shelf for the date it has - date added on To Read and Currently Reading, date
            read on Read and Did Not Finish, falling back to date added for a book that
            was never given a read date - which is the one order that reads as
            most-recent-first on every shelf at once. Publish date is the year the book
            came out, and books without one go to the end. Author files by last name, then
            by title. Titles ignore a leading "A", "An" or "The".
          </small>
        </label>
        <label className="field">
          <span>Direction</span>
          <select
            value={settings.sortDirection}
            onChange={(event) => setSettings({ ...settings, sortDirection: event.target.value })}
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </label>
        <label className="field">
          <span>Theme</span>
          <select
            value={settings.bookshelfTheme}
            onChange={(event) => setSettings({ ...settings, bookshelfTheme: event.target.value })}
          >
            <option value="classic">Classic shelf</option>
            <option value="eightBit">8-bit</option>
          </select>
          <small className="field-hint">
            Classic draws the bookshelf a room has. 8-bit lays the same books out as a
            side-scrolling game level the display walks along by itself.
          </small>
        </label>
        {/* Only Classic arranges a shelf; the level has its own idea of where a
            book stands. Hidden rather than disabled, because a control that
            does nothing still looks like one that does. The value itself is
            left alone, so switching themes and back returns the shelf to the
            layout it had. */}
        {settings.bookshelfTheme !== 'eightBit' ? (
          <label className="field">
            <span>Book layout</span>
            <select
              value={settings.bookshelfLayout}
              onChange={(event) => setSettings({ ...settings, bookshelfLayout: event.target.value })}
            >
              <option value="stacked">Stacked flat</option>
              <option value="upright">Upright spines</option>
            </select>
            <small className="field-hint">
              Stacked lays books on their sides so titles read left to right. Upright stands
              them on the shelf, which fits far more books in the same space.
            </small>
          </label>
        ) : null}
        <button type="submit" className="save-button">Save settings</button>
      </form>

    </>
  );
}
